import { useMemo } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";

import { Column, ErrorText, Row } from "@/components";
import { AutosaveFormCurrencyInput } from "@/components/companies/shared/autosave-form/AutosaveFormCurrencyInput";
import { AutosaveFormEnumMultiselect } from "@/components/companies/shared/autosave-form/AutosaveFormEnumMultiselect";
import { AutosaveFormEnumSelect } from "@/components/companies/shared/autosave-form/AutosaveFormEnumSelect";
import { AutosaveFormNumberInput } from "@/components/companies/shared/autosave-form/AutosaveFormNumberInput";
import { AutosaveFormRichTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormRichTextInput";
import { AutosaveFormTextInput } from "@/components/companies/shared/autosave-form/AutosaveFormTextInput";
import { IntakeBoolean } from "@/components/companies/shared/IntakeBoolean";
import { IntakeForm } from "@/components/companies/shared/IntakeForm";
import { ReadOnlyBooleanField, ReadOnlyField, ReadOnlyRichTextField } from "@/components/companies/shared/ReadOnlyField";
import { Label } from "@/components/ui/label";

import { useUpdateSetJobIntake } from "@/hooks/updateSets/useUpdateSetJobIntake";
import { useGetCompanyIntake } from "@/hooks/queries/useGetCompanyIntake";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import {
  employmentTypeOptions,
  employmentTypeToLabel,
  overtimeAvailabilityOptions,
  overtimeAvailabilityToLabel,
  shiftTypeOptions,
  shiftTypeToLabel,
} from "@/lib/constants";
import {
  nullableNonOptionalNumberSchema,
  nullableNumberSchema,
} from "@/lib/schemas";
import {
  CompanyIntake,
  EmploymentType,
  JobIntake,
  JobIntakeGeneralInfo,
  OvertimeAvailability,
  ShiftType,
} from "@/types";

const generalInfoSchema = z.object({
  // Role Basics
  siteId: z.string().nullable(),
  roleTitle: z.string().nullable(),
  employmentType: z.nativeEnum(EmploymentType).nullable(),
  quantityNeeded: nullableNumberSchema,
  quantityMaximum: nullableNumberSchema,
  yearsOfExperiencePreferred: z.string().nullable(),
  details: z.string().nullable(),

  // Compensation and Benefits
  compensationMinListed: nullableNonOptionalNumberSchema,
  compensationMaxListed: nullableNonOptionalNumberSchema,
  absoluteMaxCompensation: nullableNumberSchema,
  shiftDifferentialMin: nullableNumberSchema,
  shiftDifferentialMax: nullableNumberSchema,
  benefits: z.string().nullable(),

  // Work Environment
  physicalWorkConditions: z.string().nullable(),
  shiftPreferred: z.array(z.nativeEnum(ShiftType)),
  shiftFlexible: z.array(z.nativeEnum(ShiftType)),

  // Relocation
  openToRelocation: z.boolean().nullable(),
  relocationDetails: z.string().nullable(),
  relocationPackageMin: nullableNumberSchema,
  relocationPackageMax: nullableNumberSchema,

  // Background & Drug
  backgroundCheckRequired: z.boolean().nullable(),
  backgroundAndDrugTestDetails: z.string().nullable(),
  drugTestRequired: z.boolean().nullable(),

  // Overtime
  overtimeAvailable: z.boolean().nullable(),
  overtimePayMultiplier: nullableNonOptionalNumberSchema,
  overtimeAvailability: z.nativeEnum(OvertimeAvailability).nullable(),
  overtimeHoursPerWeek: z.string().nullable(),
  overtimeDetails: z.string().nullable(),
});

const Subsection = ({
  children,
  title,
}: {
  children: React.ReactNode;
  title: string;
}) => (
  <Column className="gap-2">
    <Label className="text-md font-semibold text-slate-700">{title}</Label>
    <Column className="gap-4">{children}</Column>
  </Column>
);

export const JobGeneralInfo = ({
  isEditing,
  jobIntake,
}: {
  isEditing: boolean;
  jobIntake: JobIntake;
}) => {
  /* Database CRUD Operations */
  // Read
  const { data: companyIntake } = useGetCompanyIntake();

  // Update
  const { mutate: updateJobIntake, isError } = useUpdateSetJobIntake();

  const handleSave = (data: JobIntakeGeneralInfo) => {
    mixpanelUtils.trackFormSubmission("Job general info", MIXPANEL_SCREENS.JOB);
    updateJobIntake({ updateJobIntakeRequest: { generalInfo: data } });
  };

  return (
    <Column className="gap-4">
      {isEditing ? (
        <GeneralInfoForm
          companyIntake={companyIntake}
          generalInfo={jobIntake.generalInfo}
          onSave={handleSave}
        />
      ) : (
        <GeneralInfoReadOnly
          companyIntake={companyIntake}
          generalInfo={jobIntake.generalInfo}
        />
      )}
      {isError && (
        <ErrorText errorMessage="An error occurred while saving. Please try again." />
      )}
    </Column>
  );
};

const GeneralInfoForm = ({
  companyIntake,
  generalInfo,
  onSave,
}: {
  companyIntake: CompanyIntake | null | undefined;
  generalInfo: JobIntakeGeneralInfo;
  onSave: (data: JobIntakeGeneralInfo) => void;
}) => {
  const siteOptions = useMemo(() => {
    if (!companyIntake?.sites) return [];
    return companyIntake.sites
      .filter((site) => site.id && site.siteName)
      .map((site) => ({
        value: site.id,
        label: site.siteName ?? "-",
      }));
  }, [companyIntake?.sites]);

  const form = useForm<z.infer<typeof generalInfoSchema>>({
    resolver: zodResolver(generalInfoSchema),
    defaultValues: {
      ...generalInfo,
      shiftPreferred: generalInfo.shiftPreferred ?? [],
      shiftFlexible: generalInfo.shiftFlexible ?? [],
    },
  });

  const openToRelocation = form.watch("openToRelocation");
  const backgroundCheckRequired = form.watch("backgroundCheckRequired");
  const drugTestRequired = form.watch("drugTestRequired");
  const overtimeAvailable = form.watch("overtimeAvailable");

  const saveOnBlur = () => {
    void form.handleSubmit(onSave)();
  };

  const toggle = (
    field:
      | "openToRelocation"
      | "backgroundCheckRequired"
      | "drugTestRequired"
      | "overtimeAvailable",
  ) => {
    form.setValue(field, !form.getValues(field));
    saveOnBlur();
  };

  return (
    <IntakeForm form={form} onSave={saveOnBlur}>
      <Column className="gap-8">
        {/* Role Basics */}
        <Subsection title="Role Basics">
          <Row className="gap-4">
            <AutosaveFormTextInput
              className="flex-1"
              fieldName="roleTitle"
              form={form}
              label="Role Title"
              onBlur={saveOnBlur}
              placeholder="Enter role title"
            />
            <AutosaveFormEnumSelect
              className="flex-1"
              fieldName="employmentType"
              form={form}
              label="Employment Type"
              onSelect={saveOnBlur}
              options={employmentTypeOptions}
            />
          </Row>
          <Row className="gap-4">
            <AutosaveFormNumberInput
              className="w-full"
              fieldName="quantityNeeded"
              form={form}
              label="Quantity Needed"
              onBlur={saveOnBlur}
              placeholder="Enter quantity"
            />
            <AutosaveFormNumberInput
              className="w-full"
              fieldName="quantityMaximum"
              form={form}
              label="Quantity Maximum"
              onBlur={saveOnBlur}
              placeholder="Enter maximum"
            />
          </Row>
          <Row className="gap-4">
            <AutosaveFormEnumSelect
              className="flex-1"
              emptyMessage="There are no sites for this company. Please add sites on the company intake form."
              fieldName="siteId"
              form={form}
              label="Site"
              onSelect={saveOnBlur}
              options={siteOptions}
            />
            <AutosaveFormTextInput
              className="flex-1"
              fieldName="yearsOfExperiencePreferred"
              form={form}
              label="Years of Experience (Preferred)"
              onBlur={saveOnBlur}
              placeholder="e.g., 3-5 years"
            />
          </Row>
          <AutosaveFormRichTextInput
            editorHeight="150px"
            fieldName="details"
            form={form}
            infoTooltip={
              "Is there any additional context around the role basics that doesn't fit in the fields above?\nDid the client elaborate on the title, experience level, or headcount?"
            }
            label="Details"
            onBlur={saveOnBlur}
          />
        </Subsection>

        {/* Compensation and Benefits */}
        <Subsection title="Compensation and Benefits">
          <Row className="gap-4">
            <AutosaveFormCurrencyInput
              className="w-full"
              fieldName="compensationMinListed"
              form={form}
              label="Compensation Min (Listed)"
              onBlur={saveOnBlur}
              placeholder="$0"
            />
            <AutosaveFormCurrencyInput
              className="w-full"
              fieldName="compensationMaxListed"
              form={form}
              label="Compensation Max (Listed)"
              onBlur={saveOnBlur}
              placeholder="$0"
            />
            <AutosaveFormCurrencyInput
              className="w-full"
              fieldName="absoluteMaxCompensation"
              form={form}
              label="Absolute Max Compensation"
              onBlur={saveOnBlur}
              placeholder="$0"
            />
          </Row>
          <Row className="gap-4">
            <AutosaveFormCurrencyInput
              className="w-full"
              fieldName="shiftDifferentialMin"
              form={form}
              label="Shift Differential Min"
              onBlur={saveOnBlur}
              placeholder="$0"
            />
            <AutosaveFormCurrencyInput
              className="w-full"
              fieldName="shiftDifferentialMax"
              form={form}
              label="Shift Differential Max"
              onBlur={saveOnBlur}
              placeholder="$0"
            />
            <div className="w-full" />
          </Row>
          <AutosaveFormRichTextInput
            editorHeight="150px"
            fieldName="benefits"
            form={form}
            infoTooltip={
              "What benefits are offered for this role?\nAre there any standout perks that could help attract candidates?"
            }
            label="Benefits"
            onBlur={saveOnBlur}
          />
        </Subsection>

        {/* Work Environment */}
        <Subsection title="Work Environment">
          <Row className="gap-4">
            <AutosaveFormEnumMultiselect
              className="flex-1"
              enumToLabel={shiftTypeToLabel}
              fieldName="shiftPreferred"
              form={form}
              label="Shift — Preferred"
              onSelect={saveOnBlur}
              options={shiftTypeOptions}
            />
            <AutosaveFormEnumMultiselect
              className="flex-1"
              enumToLabel={shiftTypeToLabel}
              fieldName="shiftFlexible"
              form={form}
              label="Shift — Flexible"
              onSelect={saveOnBlur}
              options={shiftTypeOptions}
            />
          </Row>
          <AutosaveFormRichTextInput
            editorHeight="150px"
            fieldName="physicalWorkConditions"
            form={form}
            infoTooltip={
              "What are the physical demands or environmental conditions of this role?\nIs there anything candidates should know before walking in on day one?"
            }
            label="Physical Work Conditions"
            onBlur={saveOnBlur}
          />
        </Subsection>

        {/* Relocation */}
        <Subsection title="Relocation">
          <IntakeBoolean
            checked={openToRelocation}
            label="Open to Relocation"
            onToggle={() => {
              toggle("openToRelocation");
            }}
          />
          <Row className="gap-4">
            <AutosaveFormCurrencyInput
              className="w-full"
              fieldName="relocationPackageMin"
              form={form}
              label="Relocation Package Min"
              onBlur={saveOnBlur}
              placeholder="$0"
            />
            <AutosaveFormCurrencyInput
              className="w-full"
              fieldName="relocationPackageMax"
              form={form}
              label="Relocation Package Max"
              onBlur={saveOnBlur}
              placeholder="$0"
            />
          </Row>
          <AutosaveFormRichTextInput
            editorHeight="100px"
            fieldName="relocationDetails"
            form={form}
            infoTooltip={
              "How willing is the client to pay out relocation — for anyone, or only for standout candidates?\nAre there any specifics around the relocation package or process?"
            }
            label="Relocation Details"
            onBlur={saveOnBlur}
          />
        </Subsection>

        {/* Background & Drug */}
        <Subsection title="Background & Drug">
          <Row className="gap-4">
            <IntakeBoolean
              checked={backgroundCheckRequired}
              className="flex-1"
              label="Background Check Required"
              onToggle={() => {
                toggle("backgroundCheckRequired");
              }}
            />
            <IntakeBoolean
              checked={drugTestRequired}
              className="flex-1"
              label="Drug Test Required"
              onToggle={() => {
                toggle("drugTestRequired");
              }}
            />
          </Row>
          <AutosaveFormRichTextInput
            editorHeight="100px"
            fieldName="backgroundAndDrugTestDetails"
            form={form}
            infoTooltip={
              "What type of drug test — 5-panel, 10-panel, hair follicle?\nWhat does the background check disqualify — felonies all-time, felonies in the past X years, misdemeanors, possession charges, etc.?"
            }
            label="Background Check and Drug Test Details"
            onBlur={saveOnBlur}
          />
        </Subsection>

        {/* Overtime */}
        <Subsection title="Overtime">
          <IntakeBoolean
            checked={overtimeAvailable}
            label="Overtime Available"
            onToggle={() => {
              toggle("overtimeAvailable");
            }}
          />
          <Row className="gap-4">
            <AutosaveFormNumberInput
              allowDecimal
              className="w-full"
              fieldName="overtimePayMultiplier"
              form={form}
              label="Pay Multiplier"
              onBlur={saveOnBlur}
              placeholder="1.5"
            />
            <AutosaveFormEnumSelect
              className="w-full"
              fieldName="overtimeAvailability"
              form={form}
              label="Availability"
              onSelect={saveOnBlur}
              options={overtimeAvailabilityOptions}
            />
            <AutosaveFormTextInput
              className="w-full"
              fieldName="overtimeHoursPerWeek"
              form={form}
              label="Hrs/week (typical)"
              onBlur={saveOnBlur}
              placeholder="e.g., 5-10"
            />
          </Row>
          <AutosaveFormRichTextInput
            editorHeight="100px"
            fieldName="overtimeDetails"
            form={form}
            infoTooltip={
              "Is overtime mandatory or voluntary?\nHow often does overtime come up — seasonal, project-based, year-round?"
            }
            label="Overtime Details"
            onBlur={saveOnBlur}
          />
        </Subsection>
      </Column>
    </IntakeForm>
  );
};

const GeneralInfoReadOnly = ({
  companyIntake,
  generalInfo,
}: {
  companyIntake: CompanyIntake | null | undefined;
  generalInfo: JobIntakeGeneralInfo;
}) => {
  const siteLabel =
    companyIntake?.sites?.find((s) => s.id === generalInfo.siteId)?.siteName ??
    null;

  const employmentTypeLabel = generalInfo.employmentType
    ? employmentTypeToLabel[generalInfo.employmentType]
    : null;
  const overtimeAvailabilityLabel = generalInfo.overtimeAvailability
    ? overtimeAvailabilityToLabel[generalInfo.overtimeAvailability]
    : null;
  const shiftPreferredLabel =
    (generalInfo.shiftPreferred ?? []).length > 0
      ? (generalInfo.shiftPreferred ?? [])
          .map((s) => shiftTypeToLabel[s])
          .join(", ")
      : null;
  const shiftFlexibleLabel =
    (generalInfo.shiftFlexible ?? []).length > 0
      ? (generalInfo.shiftFlexible ?? [])
          .map((s) => shiftTypeToLabel[s])
          .join(", ")
      : null;

  const formatCurrency = (val?: number | null) =>
    val != null ? `$${val.toLocaleString()}` : null;

  return (
    <Column className="gap-12">
      <Column className="gap-2">
        <Label className="text-md font-semibold text-slate-700">
          Role Basics
        </Label>
        <Column className="gap-4">
          <Row className="gap-4">
            <ReadOnlyField
              className="flex-1"
              label="Role Title"
              value={generalInfo.roleTitle ?? null}
            />
            <ReadOnlyField
              className="flex-1"
              label="Employment Type"
              value={employmentTypeLabel}
            />
          </Row>
          <Row className="gap-4">
            <ReadOnlyField
              className="flex-1"
              label="Quantity Needed"
              value={
                generalInfo.quantityNeeded != null
                  ? String(generalInfo.quantityNeeded)
                  : null
              }
            />
            <ReadOnlyField
              className="flex-1"
              label="Quantity Maximum"
              value={
                generalInfo.quantityMaximum != null
                  ? String(generalInfo.quantityMaximum)
                  : null
              }
            />
          </Row>
          <Row className="gap-4">
            <ReadOnlyField className="flex-1" label="Site" value={siteLabel} />
            <ReadOnlyField
              className="flex-1"
              label="Years of Experience (Preferred)"
              value={generalInfo.yearsOfExperiencePreferred ?? null}
            />
          </Row>
          <ReadOnlyRichTextField
            label="Details"
            value={generalInfo.details ?? null}
          />
        </Column>
      </Column>

      <Column className="gap-2">
        <Label className="text-md font-semibold text-slate-700">
          Compensation and Benefits
        </Label>
        <div className="grid grid-cols-3 gap-4">
          <ReadOnlyField
            label="Comp Min (Listed)"
            value={formatCurrency(generalInfo.compensationMinListed)}
          />
          <ReadOnlyField
            label="Comp Max (Listed)"
            value={formatCurrency(generalInfo.compensationMaxListed)}
          />
          <ReadOnlyField
            label="Absolute Max"
            value={formatCurrency(generalInfo.absoluteMaxCompensation)}
          />
          <ReadOnlyField
            label="Shift Diff Min"
            value={formatCurrency(generalInfo.shiftDifferentialMin)}
          />
          <ReadOnlyField
            label="Shift Diff Max"
            value={formatCurrency(generalInfo.shiftDifferentialMax)}
          />
          <div />
          <ReadOnlyRichTextField
            className="col-span-3"
            label="Benefits"
            value={generalInfo.benefits ?? null}
          />
        </div>
      </Column>

      <Column className="gap-2">
        <Label className="text-md font-semibold text-slate-700">
          Work Environment
        </Label>
        <Column className="gap-4">
          <Row className="gap-4">
            <ReadOnlyField
              className="flex-1"
              label="Shift — Preferred"
              value={shiftPreferredLabel}
            />
            <ReadOnlyField
              className="flex-1"
              label="Shift — Flexible"
              value={shiftFlexibleLabel}
            />
          </Row>
          <ReadOnlyRichTextField
            label="Physical Work Conditions"
            value={generalInfo.physicalWorkConditions ?? null}
          />
        </Column>
      </Column>

      <Column className="gap-2">
        <Label className="text-md font-semibold text-slate-700">
          Relocation
        </Label>
        <div className="grid grid-cols-3 gap-4">
          <ReadOnlyBooleanField
            label="Open to Relocation"
            isSelected={generalInfo.openToRelocation}
          />
          <div />
          <div />
          <ReadOnlyField
            label="Package Min"
            value={formatCurrency(generalInfo.relocationPackageMin)}
          />
          <ReadOnlyField
            label="Package Max"
            value={formatCurrency(generalInfo.relocationPackageMax)}
          />
          <div />
          <ReadOnlyRichTextField
            className="col-span-3"
            label="Relocation Details"
            value={generalInfo.relocationDetails ?? null}
          />
        </div>
      </Column>

      <Column className="gap-2">
        <Label className="text-md font-semibold text-slate-700">
          Background & Drug
        </Label>
        <div className="grid grid-cols-3 gap-4">
          <ReadOnlyBooleanField
            label="Background Check Required"
            isSelected={generalInfo.backgroundCheckRequired}
          />
          <ReadOnlyBooleanField
            label="Drug Test Required"
            isSelected={generalInfo.drugTestRequired}
          />
          <div />
          <ReadOnlyRichTextField
            className="col-span-3"
            label="Background Check and Drug Test Details"
            value={generalInfo.backgroundAndDrugTestDetails ?? null}
          />
        </div>
      </Column>

      <Column className="gap-2">
        <Label className="text-md font-semibold text-slate-700">Overtime</Label>
        <div className="grid grid-cols-3 gap-4">
          <ReadOnlyBooleanField
            label="Overtime Available"
            isSelected={generalInfo.overtimeAvailable}
          />
          <div />
          <div />
          <ReadOnlyField
            label="Pay Multiplier"
            value={
              generalInfo.overtimePayMultiplier != null
                ? String(generalInfo.overtimePayMultiplier)
                : null
            }
          />
          <ReadOnlyField
            label="Availability"
            value={overtimeAvailabilityLabel}
          />
          <ReadOnlyField
            label="Hrs/week (typical)"
            value={generalInfo.overtimeHoursPerWeek ?? null}
          />
          <ReadOnlyRichTextField
            className="col-span-3"
            label="Overtime Details"
            value={generalInfo.overtimeDetails ?? null}
          />
        </div>
      </Column>
    </Column>
  );
};
